import { useId, useState } from 'react';

import CustomerRelationshipPositionComposition
  from './CustomerRelationshipPositionComposition';

import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import {
  formatMarginCenterCurrency,
  formatMarginCenterNumber,
} from '../utils/numberFormat';

import type {
  MarginCenterCustomerProductPositioning,
  MarginCenterCustomerProductPositioningPeriod,
  MarginCenterCustomerProductSelection,
} from '../types/marginCenter';

type Side = 'customer' | 'product';

function pct(value: number | null): string {
  return value === null ? '—' : `${formatMarginCenterNumber(value)} %`;
}

function signedPoints(value: number | null): string {
  if (value === null) return '—';
  return `${value > 0 ? '+' : ''}${formatMarginCenterNumber(value)} p.p.`;
}

function changeColor(value: number | null): string {
  if (value === null || value === 0) return '#94a3b8';
  return value > 0 ? '#34d399' : '#fb7185';
}

function values(period: MarginCenterCustomerProductPositioningPeriod, side: Side) {
  return side === 'customer'
    ? {
        share: period.relationship_share_of_customer_revenue_pct,
        total: period.customer_total_revenue,
        rank: period.product_rank_within_customer,
        active: period.customer_active_products,
      }
    : {
        share: period.relationship_share_of_product_revenue_pct,
        total: period.product_total_revenue,
        rank: period.customer_rank_within_product,
        active: period.product_active_customers,
      };
}

function PositionCard({
  title,
  rankKind,
  share,
  rank,
  active,
  change,
  selected,
  detailId,
  onClick,
}: {
  title: string;
  rankKind: string;
  share: number | null;
  rank: number | null;
  active: number;
  change: number | null;
  selected: boolean;
  detailId: string;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-expanded={selected}
      aria-controls={detailId}
      aria-label={`${title}. ${pct(share)}. ${selected ? 'Ocultar' : 'Ver'} composición y evolución.`}
      sx={{
        minWidth: 0,
        width: '100%',
        display: 'block',
        textAlign: 'left',
        p: 1.25,
        borderRadius: 1.5,
        border: selected ? '1px solid rgba(56,189,248,0.52)' : '1px solid rgba(148,163,184,0.14)',
        bgcolor: selected ? 'rgba(56,189,248,0.07)' : 'rgba(15,23,42,0.42)',
        '&:hover': { bgcolor: 'rgba(56,189,248,0.06)', borderColor: 'rgba(56,189,248,0.35)' },
        '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: 2 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
          {title}
        </Typography>
        {selected
          ? <KeyboardArrowDownRoundedIcon sx={{ color: '#7dd3fc', fontSize: 18 }} />
          : <KeyboardArrowRightRoundedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
      </Stack>
      <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
        {pct(share)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
        {rank === null ? 'Sin posición actual' : `#${formatMarginCenterNumber(rank, 0)} de ${formatMarginCenterNumber(active, 0)} ${rankKind} activos`}
      </Typography>
      {change !== null && (
        <Typography variant="caption" sx={{ display: 'block', color: changeColor(change), fontWeight: 800, mt: 0.35 }}>
          Cambio de peso: {signedPoints(change)}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: selected ? '#7dd3fc' : '#94a3b8', fontWeight: 800 }}>
        {selected ? 'Ocultar composición' : 'Ver composición'}
      </Typography>
    </ButtonBase>
  );
}

function PeriodEvidence({
  title,
  side,
  period,
  currencyCode,
}: {
  title: string;
  side: Side;
  period: MarginCenterCustomerProductPositioningPeriod;
  currencyCode: string | null;
}) {
  const item = values(period, side);
  const totalLabel = side === 'customer' ? 'Ingresos totales del cliente' : 'Ingresos totales del producto';
  const rankKind = side === 'customer' ? 'productos' : 'clientes';
  return (
    <Box sx={{ p: 1.15, minWidth: 0, borderRadius: 1.5, bgcolor: 'rgba(15,23,42,0.40)', border: '1px solid rgba(148,163,184,0.12)' }}>
      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
        {title}
      </Typography>
      <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
        {pct(item.share)}
      </Typography>
      <Box sx={{ mt: 0.6, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 0.6 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>Ingresos de esta relación</Typography>
        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
          {formatMarginCenterCurrency(period.relationship_revenue, currencyCode)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{totalLabel}</Typography>
        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
          {formatMarginCenterCurrency(item.total, currencyCode)}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.7, color: '#94a3b8' }}>
        {item.rank === null ? 'Sin puesto calculable' : `Puesto #${formatMarginCenterNumber(item.rank, 0)} de ${formatMarginCenterNumber(item.active, 0)} ${rankKind} activos`}
      </Typography>
    </Box>
  );
}

export default function CustomerRelationshipPositionPanel({
  positioning,
  currencyCode,
  customerId,
  productId,
  customerLabel,
  productLabel,
  selection,
}: {
  positioning: MarginCenterCustomerProductPositioning;
  currencyCode: string | null;
  customerId: string;
  productId: string;
  customerLabel: string;
  productLabel: string;
  selection: MarginCenterCustomerProductSelection;
}) {
  const [selected, setSelected] = useState<Side | null>(null);
  const detailId = useId();
  const side: Side = selected ?? 'customer';
  const [showMethod, setShowMethod] = useState(false);
  const change = positioning.changes
    ? (side === 'customer'
        ? positioning.changes.share_of_customer_delta_points
        : positioning.changes.share_of_product_delta_points)
    : null;
  const denominator = side === 'customer' ? 'cliente' : 'producto';
  const rankKind = side === 'customer' ? 'productos' : 'clientes';

  return (
    <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, border: '1px solid rgba(129,140,248,0.14)', background: 'linear-gradient(180deg, rgba(18,27,47,0.68) 0%, rgba(10,18,33,0.84) 100%)' }}>
      <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 850 }}>Posición comercial</Typography>
      <Typography variant="caption" sx={{ color: '#64748b' }}>Peso de esta relación en los ingresos del cliente y del producto.</Typography>
      <Box sx={{ mt: 1.15, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
        <PositionCard
          title="Este producto · Ingresos del cliente"
          rankKind="productos"
          {...values(positioning.current, 'customer')}
          change={positioning.changes?.share_of_customer_delta_points ?? null}
          selected={selected === 'customer'}
          detailId={detailId}
          onClick={() => setSelected((prev) => prev === 'customer' ? null : 'customer')}
        />
        <PositionCard
          title="Este cliente · Ingresos del producto"
          rankKind="clientes"
          {...values(positioning.current, 'product')}
          change={positioning.changes?.share_of_product_delta_points ?? null}
          selected={selected === 'product'}
          detailId={detailId}
          onClick={() => setSelected((prev) => prev === 'product' ? null : 'product')}
        />
      </Box>
      <Collapse in={selected !== null} unmountOnExit>
        <Box id={detailId} role="region" aria-label="Composición de la posición comercial" sx={{ mt: 1.1, p: { xs: 1.1, md: 1.4 }, borderRadius: 1.6, border: '1px solid rgba(56,189,248,0.27)', bgcolor: 'rgba(12,28,46,0.72)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Box>
              <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 850 }}>Composición y evolución</Typography>
              <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>
                {side === 'customer' ? 'Este producto dentro del cliente' : 'Este cliente dentro del producto'}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => setSelected(null)} sx={{ textTransform: 'none', color: '#94a3b8', borderColor: 'rgba(148,163,184,0.30)' }}>Cerrar</Button>
          </Stack>
          <CustomerRelationshipPositionComposition
            key={`${side}:${selection.start_date}:${selection.end_date}:${selection.comparison_mode}:${selection.comparison_start_date}:${selection.comparison_end_date}`}
            side={side}
            customerId={customerId}
            productId={productId}
            customerLabel={customerLabel}
            productLabel={productLabel}
            positioning={positioning}
            selection={selection}
            currencyCode={currencyCode}
          />
          <Button size="small" variant="text" onClick={() => setShowMethod((prev) => !prev)}
            sx={{ mt: 0.8, px: 0, textTransform: 'none', color: '#7dd3fc' }}>
            {showMethod ? 'Ocultar cálculo y comparación' : 'Ver cálculo y comparación'}
          </Button>
          <Collapse in={showMethod} unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: positioning.comparison ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 1 }}>
              <PeriodEvidence title="Periodo analizado" side={side} period={positioning.current} currencyCode={currencyCode} />
              {positioning.comparison && (
                <PeriodEvidence title="Periodo comparado" side={side} period={positioning.comparison} currencyCode={currencyCode} />
              )}
            </Box>
            {positioning.comparison && (
              <Typography variant="body2" sx={{ mt: 0.85, color: changeColor(change), fontWeight: 800 }}>
                {change === null ? 'Cambio de peso no calculable.' : `Cambio del peso: ${signedPoints(change)}.`}
              </Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.65, color: '#94a3b8' }}>
              Peso = ingresos de esta relación / ingresos totales del {denominator}. El puesto se calcula por ingresos entre los {rankKind} activos del periodo.
            </Typography>
          </Collapse>
        </Box>
      </Collapse>
    </Paper>
  );
}
